import styles from './DateTimePickerField.module.scss';

import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { LabeledDateInputWithDropdown } from '../../input/labeled-date-input';
import { LabeledTimeInputWithDropdown } from '../../input/labeled-time-input';

import {
  useDateTimePickerFieldDropdownStatus,
  useDateTimePickerFieldEventsHandlers,
  useDateTimePickerFieldValues,
} from './hooks';
import { DateTimePickerFieldProps } from './types';

export const DateTimePickerField = ({
  value = '',
  onChange,
  clearable = false,
  dateLabel,
  timeLabel,
  hideTime = false,
}: DateTimePickerFieldProps) => {
  const { t } = useTranslation();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { dateString, timeString } = useDateTimePickerFieldValues({ value });

  const {
    isDateDropdownOpen,
    isTimeDropdownOpen,
    openDateDropdown,
    openTimeDropdown,
    closeDateDropdown,
    closeTimeDropdown,
  } = useDateTimePickerFieldDropdownStatus();

  const {
    onDateSelect,
    onDateChange,
    onDateBlur,
    onTimeSelect,
    onTimeChange,
    onTimeBlur,
    onClear,
    handleEnterEscapeTabKeys,
  } = useDateTimePickerFieldEventsHandlers({ onChange, clearable, value });

  const hideTimeField = hideTime || !value;

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <LabeledDateInputWithDropdown
        label={dateLabel ?? t('COMMON.DATE')}
        value={dateString}
        anchorEl={wrapperRef.current}
        onClear={onClear}
        onBlur={onDateBlur}
        onChange={onDateChange}
        onKeyDown={handleEnterEscapeTabKeys(closeDateDropdown)}
        onDateSelect={onDateSelect}
        onIconClick={openDateDropdown}
        showDropdown={isDateDropdownOpen}
        onHideDropdown={closeDateDropdown}
        onShowDropdown={(e) => {
          e.preventDefault();
          openDateDropdown();
        }}
      />

      {hideTimeField ? null : (
        <LabeledTimeInputWithDropdown
          label={timeLabel ?? t('COMMON.TIME')}
          value={timeString}
          anchorEl={wrapperRef.current}
          onClear={onClear}
          onBlur={onTimeBlur}
          onChange={onTimeChange}
          onTimeSelect={onTimeSelect}
          onKeyDown={handleEnterEscapeTabKeys(closeTimeDropdown)}
          showDropdown={isTimeDropdownOpen}
          onHideDropdown={closeTimeDropdown}
          onShowDropdown={(e) => {
            e.preventDefault();
            openTimeDropdown();
          }}
        />
      )}
    </div>
  );
};
